import { uiText } from '../../i18n/uiText';
import { notificationText } from '../../utils/notificationText';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  AlertTriangle,
  ArrowRight,
  Bell,
  Building2,
  CheckCheck,
  ClipboardCheck,
  FileCheck,
  KeyRound,
  Loader2,
  Mail,
  MessageSquare,
  ShieldAlert,
} from 'lucide-react';
import { useLocation, useNavigate } from 'react-router-dom';
import { getApiErrorMessage, notificationsApi } from '../../api';
import { formatNotificationTime, moscowDay } from '../../utils/formatters';
import { safeInternalRoute } from '../../utils/safeNavigation';

const ICONS = {
  access_request: KeyRound,
  assignment: ClipboardCheck,
  status: ShieldAlert,
  comment: MessageSquare,
  issue_message: MessageSquare,
  registration: Building2,
  invitation: Mail,
  self_check: FileCheck,
  erp_flag: AlertTriangle,
};

function resolveNotificationHref(item) {
  if (typeof item?.href === 'string') return safeInternalRoute(item.href, '/notifications');
  if (item?.meta?.audit_id && item.kind === 'comment') return `/audits/${item.meta.audit_id}?tab=comments`;
  if (item?.meta?.audit_id && item.kind === 'status') return `/audits/${item.meta.audit_id}?tab=history`;
  if (item?.meta?.audit_id && item.kind === 'issue_message') return `/audits/${item.meta.audit_id}?tab=issues`;
  if (item?.kind === 'assignment') return '/assignment-notifications';
  if (item?.kind === 'registration') return '/users';
  if (item?.kind === 'self_check' && item?.meta?.company_id) return `/self-check/review/${item.meta.company_id}`;
  return '/notifications';
}

function groupItems(items) {
  const today = [];
  const yesterday = [];
  const earlier = [];
  const now = new Date();
  const startToday = moscowDay(now);
  const startYesterday = moscowDay(new Date(now.getTime() - 86400000));
  items.forEach((item) => {
    const created = moscowDay(item?.created_at);
    if (!created) {
      earlier.push(item);
    } else if (created === startToday) {
      today.push(item);
    } else if (created === startYesterday) {
      yesterday.push(item);
    } else {
      earlier.push(item);
    }
  });
  return [
    { key: 'today', label: uiText("Сегодня"), items: today },
    { key: 'yesterday', label: uiText("Вчера"), items: yesterday },
    { key: 'earlier', label: uiText("Ранее"), items: earlier },
  ].filter((group) => group.items.length);
}

export default function NotificationsBell({ showLabel = false }) {
  const navigate = useNavigate();
  const location = useLocation();
  const rootRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [items, setItems] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);

  const loadNotifications = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const res = await notificationsApi.list({ limit: 12, unread_only: false, kind: 'all' });
      setItems(res.data?.items || []);
      setUnreadCount(Number(res.data?.unread_count || 0));
      setError('');
    } catch (err) {
      setError(getApiErrorMessage(err));
    } finally {
      if (!silent) setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadNotifications();
    const timer = window.setInterval(() => loadNotifications(true), 30000);
    return () => window.clearInterval(timer);
  }, [loadNotifications]);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (rootRef.current && !rootRef.current.contains(event.target)) {
        setOpen(false);
      }
    };
    const handleEscape = (event) => {
      if (event.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, []);

  const unreadLabel = useMemo(() => (unreadCount > 99 ? '99+' : `${unreadCount}`), [unreadCount]);
  const groupedItems = useMemo(() => groupItems(items), [items]);

  const handleToggle = async () => {
    const next = !open;
    setOpen(next);
    if (next) await loadNotifications();
  };

  const handleItemClick = async (item) => {
    try {
      if (item.unread) {
        await notificationsApi.markRead({ source_type: item.source_type, source_id: item.source_id });
        setItems((prev) => prev.map((entry) => (entry.id === item.id ? { ...entry, unread: false } : entry)));
        setUnreadCount((prev) => Math.max(0, prev - 1));
      }
    } catch {
      // no-op
    }
    setOpen(false);
    navigate(resolveNotificationHref(item));
  };

  const handleMarkAllRead = async () => {
    try {
      await notificationsApi.markAllRead();
      setItems((prev) => prev.map((item) => ({ ...item, unread: false })));
      setUnreadCount(0);
    } catch (err) {
      setError(getApiErrorMessage(err));
    }
  };

  return (
    <div className="workspace-notifications" ref={rootRef}>
      <button type="button" className={`workspace-icon-btn workspace-bell-btn ${showLabel ? 'workspace-top-action has-label' : ''} ${unreadCount > 0 ? 'has-unread' : ''} ${open ? 'is-open' : ''}`} title={uiText("Уведомления")} aria-label={unreadCount > 0 ? uiText("Уведомления: {v0} новых", {v0: (unreadCount)}) : uiText("Уведомления")} onClick={handleToggle}>
        <Bell size={16} />
        {showLabel ? <span className="workspace-top-action-label">{uiText("Уведомления")}</span> : null}
        {unreadCount > 0 ? <span className="workspace-bell-count" aria-hidden="true">{unreadLabel}</span> : null}
      </button>

      {open ? (
        <div className="workspace-notifications-panel card" role="dialog" aria-label={uiText("Уведомления")}>
          <div className="workspace-notifications-header">
            <div>
              <div className="workspace-item-title">{uiText("Уведомления")}</div>
              <div className="workspace-muted">{unreadCount > 0 ? uiText("{v0} новых", {v0: (unreadCount)}) : uiText("Новых уведомлений нет")}</div>
            </div>
            <button type="button" className="btn btn-ghost btn-sm" onClick={handleMarkAllRead} disabled={!items.length || unreadCount === 0}>
              <CheckCheck size={14} /> {unreadCount > 0 ? uiText("Прочитать всё") : uiText("Всё прочитано")}
            </button>
          </div>

          {loading ? (
            <div className="workspace-notifications-state"><Loader2 size={16} className="spin" />{uiText(" Загрузка…")}</div>
          ) : error ? (
            <div className="workspace-notifications-state is-error">{error}</div>
          ) : !items.length ? (
            <div className="workspace-notifications-state">{uiText("Сюда будут приходить назначения, ответы и новые сообщения.")}</div>
          ) : (
            <div className="workspace-notifications-list workspace-notifications-list-grouped">
              {groupedItems.map((group) => (
                <div key={group.key} className="workspace-notification-group workspace-notification-group-compact">
                  <div className="workspace-notification-group-title">{group.label}</div>
                  <div className="workspace-list">
                    {group.items.map((item) => {
                      const Icon = ICONS[item.kind] || Bell;
                      const fallback = resolveNotificationHref(item) === '/notifications';
                      return (
                        <button key={item.id} type="button" className={`workspace-notification-item ${item.unread ? 'is-unread' : ''}`} onClick={() => handleItemClick(item)}>
                          <span className="workspace-notification-kind"><Icon size={15} /></span>
                          <div className="workspace-notification-content">
                            <div className="workspace-notification-topline">
                              <span className="workspace-notification-title">{uiText(item.title)}</span>
                              <span className="workspace-notification-date">{formatNotificationTime(item.created_at)}</span>
                            </div>
                            {item.actor?.full_name ? <div className="workspace-notification-actor">{item.actor.full_name}</div> : null}
                            <div className="workspace-notification-text">{notificationText(item) || uiText("Без текста")}</div>
                            {fallback ? <div className="workspace-notification-fallback">{uiText("Источник недоступен. Откроется общий центр уведомлений.")}</div> : null}
                          </div>
                          {item.unread ? <span className="workspace-notification-dot" /> : null}
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          )}

          <div className="workspace-notifications-footer">
            <button type="button" className="btn btn-ghost btn-sm" onClick={() => { setOpen(false); navigate('/notifications'); }}>{uiText("Все уведомления ")}<ArrowRight size={14} />
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}
